import { Box, Container, Snackbar, TextField } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { mutationKeys } from '../../services/react-query/mutationKeys';
import { useGetCurrentOrganization, useUpdateCurrentOrganization } from '../../services/react-query/organization';
import { useGetCurrentUser } from '../../services/react-query/users/useGetCurrentUser';
import { inputDividerStyling } from '../../styles/sxStyling';
import { UserSettingsCollapseKeys } from '../../types/User';
import { Organization } from '../../types/organization';
import { Loading } from '../../uikit';
import { errorPopulator } from '../../utility/inputUtils';
import AccordionSection from '../AccordionSection';
import ItemHeader from '../common/ItemHeader/ItemHeader';
import OrganizationSettingsForm from './OrganizationSettingsForm';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { JsonifyFile } from '../../utility/functions';

const OrganizationForm: React.FC = () => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const { data: currentUser } = useGetCurrentUser();
  const { data: organizationData, isLoading: isGetOrganizationByIdLoading } = useGetCurrentOrganization({
    enabled: !_.isNil(currentUser?.organization_uuid),
  });

  const isUpdateOrganizationMutation = useIsMutating({ mutationKey: [mutationKeys.updateOrganization] });
  const { mutate: updateOrganization } = useUpdateCurrentOrganization({
    onSuccessCallback: () => setSaved(true),
  });

  useEffect(() => {
    if (organizationData) {
      setOrganization(organizationData);
    }
  }, [organizationData]);

  const { disabled } = useContext(DisableContext);

  const updateOrganizationData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    setOrganization((prev) => (prev ? { ...prev, [id]: value } : prev));
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const jsonifiedFile = await JsonifyFile(file);
    setOrganization((prev) => (prev ? { ...prev, logo: jsonifiedFile } : prev));
  };

  const organizationTitle = organization?.name ?? organizationData?.name ?? '';

  const handleSave = () => {
    if (organization) {
      updateOrganization(organization);
    }
  };

  if (isGetOrganizationByIdLoading) {
    return <Loading />;
  }

  const logo = (
    organizationData?.logo_url &&
    <Box component="img" src={organizationData.logo_url} sx={{ height: 30, width: 'fit-content' }} />
  );

  return (
    <Container>
      <ItemHeader
        title={organizationTitle}
        onSave={handleSave}
        disableActions={Boolean(isUpdateOrganizationMutation)}
        icon={logo}
      />

      <AccordionSection title="Organization Information" collapseId={UserSettingsCollapseKeys.ORGANIZATION_ITEM_INFORMATION}>
        <Box sx={inputDividerStyling}>
          <TextField
            id="name"
            label="Name"
            autoFocus
            onChange={updateOrganizationData}
            value={organization?.name ?? ''}
            disabled={disabled}
            {...errorPopulator(organization, 'name')}
          />
          <TextField
            id="email"
            label="Email"
            onChange={updateOrganizationData}
            value={organization?.email ?? ''}
            disabled={disabled}
            {...errorPopulator(organization, 'email')}
          />
          <TextField
            id="phone"
            label="Phone"
            onChange={updateOrganizationData}
            value={organization?.phone ?? ''}
            disabled={disabled}
            {...errorPopulator(organization, 'phone')}
          />
          <TextField
            type="file"
            label="Logo"
            slotProps={{
              input: { inputProps: { accept: 'image/*' } },
              inputLabel: { shrink: true },
            }}
            onChange={handleLogoChange}
            disabled={disabled}
          />
        </Box>
      </AccordionSection>

      <OrganizationSettingsForm organization={organization} setSaved={setSaved} />

      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Container>
  );
};

export default OrganizationForm;
